import { loopedPairKind, registerPairClip, type PairApproach } from './pair-types.js';

/**
 * A two-character animation authored in Blender: both bodies' joint tracks, baked at a fixed frame rate, plus where each character stands.
 * It is data only. `npm run animation:import` writes one per clip from the glTF Blender exports (see `docs/animation-authoring.md`); the runtime
 * player (`authored-pair.ts`) blends the two units into the first frame, plays it, and blends them out.
 *
 * Frames are samples at `fps`, from frame 0 (the initial keyframe the game blends into) to the last. All flat arrays are frame-major.
 */
export interface AuthoredRoleTracks {
  /** The skeleton this role was authored on (`human-male` or `human-female`). */
  body: string;
  /** Where the character stands in the pair's own space (the glTF axes: +y up, metres), per frame: x, y, z and yaw (radians about +y). The game puts the pair's midpoint on the real pair's midpoint. */
  root: number[];
  /** Pitch and roll (radians) of the whole figure per frame when it is not upright (prone, leaning, rolling): the turn is yaw, then pitch about the figure's own x, then roll about its z (three.js `YXZ`). Absent when the character stays upright. */
  tilt?: number[];
  /** Local rotation (quaternion x, y, z, w) and, when it moves, position (x, y, z) of each joint that is animated, by the rig's joint name: the hips' local position, any other joint's offset from its rest place in its parent's space (the collarbones, neck and head; scaled to the trunk of the body that plays it). A joint that is not here keeps its rest pose. */
  joints: Record<string, { q?: number[]; p?: number[] }>;
}
export interface AuthoredPairClip {
  id: string;
  fps: number;
  /** Number of frames; the clip lasts `(frames - 1) / fps` seconds. */
  frames: number;
  seconds: number;
  /** The distance between the two characters at frame 0 (m). */
  spacing: number;
  /**
   * Where the walker stops before the blend into frame 0: the direction from the partner (from the way they face) and the distance. Read from
   * the two characters' places at frame 0 unless the import overrides it, so a clip that starts with the first character 1 m behind the second
   * is approached from behind, 1 m away. When the distance is more than `spacing`, the blend closes the difference.
   */
  approach: PairApproach;
  /** Seconds the pair settle, blending into frame 0, and seconds they blend back out of the last frame when they part. */
  settle: number;
  heroesOnly: boolean;
  roles: { first: AuthoredRoleTracks; second: AuthoredRoleTracks };
}
const clips = new Map<string, AuthoredPairClip>();
/** Registers an authored clip as a kind of meeting, for the server (length, spacing) and the animators (tracks). */
export function registerAuthoredPairClip(clip: AuthoredPairClip) {
  clips.set(clip.id, clip);
  // A clip imported before approaches existed has none: it is approached from the front, at the distance it starts.
  const approach = clip.approach ?? { angle: 0, distance: clip.spacing };
  const info = { seconds: clip.seconds, spacing: approach.distance, approach, settle: clip.settle, heroesOnly: clip.heroesOnly };
  // Every clip is two kinds of meeting: the one-shot (approach, settle, play, part) and its looped version, which plays until it is ended.
  registerPairClip(clip.id, info);
  registerPairClip(loopedPairKind(clip.id), { ...info, loops: true });
  clips.set(loopedPairKind(clip.id), clip);
}
/** The clip a kind of meeting plays: the same tracks for `my-clip` and `my-clip:loop`. */
export function authoredPairClip(id: string): AuthoredPairClip | undefined { return clips.get(id); }
export function authoredPairClips(): AuthoredPairClip[] { return [...clips.entries()].filter(([id, clip]) => id === clip.id).map(([, clip]) => clip); }
